import { useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Select,
  Upload,
  message,
} from "antd";
import { localUploadBaseProps } from "../../../../utils/localUploadRequest";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { UploadProps } from "antd";
import DrawerFooter from "../../../../components/layout/drawer/DrawerFooter";
import InputCustom from "../../../../components/custom/InputCustom";
import { useUploadFileMutation } from "../../../../graphql/operations/upload-file";
import { AfBlobPath } from "../../../../graphql/operations/@types";
import { renderAntDMessageConfig } from "../../../../utils/renderAntDMessageConfig";

interface GoGoLiveDrawerProps {
  show: boolean;
  form: any;
  setShow: any;
  handleConfirm: (values: any) => void;
  setHkPdfFileName: any;
  setKrPdfFileName: any;
  setTwPdfFileName: any;
  setPosterFileName: any;
  btnLoading: boolean;
}

export const renderPosterCAPLevel = (level: string | undefined) => {
  if (level === "LightBlue") {
    return level.toLowerCase().replace("tb", "t_b");
  }
  return level?.toLowerCase();
};

const GoGoLiveCreateDrawer = ({
  show,
  form,
  setShow,
  handleConfirm,
  setHkPdfFileName,
  setKrPdfFileName,
  setTwPdfFileName,
  setPosterFileName,
  btnLoading,
}: GoGoLiveDrawerProps) => {
  const [selectYearMonth, setSelectYearMonth] = useState<string | undefined>();
  const [selectCAPLevel, setSelectCAPLevel] = useState<string | undefined>();
  const [uploadFile] = useUploadFileMutation();
  // const videoProps: UploadProps = {
  //   name: "file",
  //   action: "https://httpbin.org/post",
  //   headers: {
  //     authorization: "authorization-text",
  //   },
  //   beforeUpload: (file) => {
  //     const isVideo = file.type.startsWith("video");
  //     if (!isVideo) {
  //       message.error("Only upload video file!");
  //     }
  //     let reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => {
  //       console.log(URL.createObjectURL(file));
  //     };
  //     return isVideo || Upload.LIST_IGNORE;
  //   },
  //   onChange(info) {
  //     setUploadedVideoFileName(info.file.name);
  //     if (info.file.status !== "uploading") {
  //       console.log(info.file, info.fileList);
  //     }
  //     if (info.file.status === "done") {
  //       message.success(`${info.file.name} file uploaded successfully`);
  //     } else if (info.file.status === "error") {
  //       message.error(`${info.file.name} file upload failed.`);
  //     }
  //   },
  //   maxCount: 1,
  //   onRemove: () => setUploadedVideoFileName(""),
  // };

  const pdfProps: UploadProps = {
    ...localUploadBaseProps,
    beforeUpload: (file) => {
      const isPdf = file.type.split("/")[1] === "pdf";
      if (!isPdf) {
        message.error(renderAntDMessageConfig("Only upload pdf file!"));
      }
      return isPdf || Upload.LIST_IGNORE;
    },
    maxCount: 1,
  };

  const posterProps: UploadProps = {
    ...localUploadBaseProps,
    beforeUpload: (file) => {
      const newFileName = `${selectYearMonth}_${renderPosterCAPLevel(
        selectCAPLevel
      )}.png`;
      const newFile = new File([file], newFileName, { type: file.type });
      const isPNG = file.type.split("/")[1] === "png";
      const isLt200K = file.size / 1024 <= 200;
      const hadYearMonth = selectYearMonth ? true : false;
      const hadCAPLevel = selectCAPLevel ? true : false;
      if (!selectYearMonth || !selectCAPLevel) {
        message.error(
          renderAntDMessageConfig(
            "Please select Year month and CAP level first"
          )
        );
      }
      if (!isPNG) {
        message.error(renderAntDMessageConfig("Only upload PNG file!"));
      }
      if (!isLt200K) {
        message.error(
          renderAntDMessageConfig(
            "Please upload an image that is less than 200KB."
          )
        );
      }
      return (
        (isPNG && isLt200K && hadCAPLevel && hadYearMonth && newFile) ||
        Upload.LIST_IGNORE
      );
    },
    onChange: (info) => {
      console.log(info.fileList);
      if (info.fileList.length === 0) {
        setPosterFileName(undefined);
        return;
      }
      if (info.file.status === "done") {
        setPosterFileName(info.file.name);
        uploadFile({
          variables: {
            blobs: [
              {
                file: info.file.originFileObj,
                path: AfBlobPath.GoGoLiveImage,
              },
            ],
          },
        }).catch((e) => console.log(e));
        message.success(
          renderAntDMessageConfig(
            `${info.file.name} file uploaded successfully`
          )
        );
      } else if (info.file.status === "error") {
        message.error(
          renderAntDMessageConfig(`${info.file.name} file upload failed.`)
        );
      }
    },
    maxCount: 1,
    onRemove: () => setPosterFileName(""),
  };

  return (
    <Drawer
      open={show}
      onClose={() => {
        form.resetFields();
        setShow(false);
        setHkPdfFileName(undefined);
        setKrPdfFileName(undefined);
        setTwPdfFileName(undefined);
        setPosterFileName(undefined);
        setSelectYearMonth(undefined);
        setSelectCAPLevel(undefined);
      }}
      title="Create new Go, Go Live! content"
      width={500}
    >
      <Form
        name="ggl-form"
        form={form}
        onFinish={handleConfirm}
        layout="vertical"
        className="ggl-form"
      >
        <Form.Item
          label="Year month"
          name="yearMonth"
          rules={[
            {
              required: true,
              message: "This field is required!",
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%", marginTop: 8 }}
            format="YYYY-MMM"
            picker="month"
            disabledDate={(current) => {
              return (
                current && current < dayjs().endOf("month").subtract(1, "month")
              );
            }}
            onChange={(value) =>
              setSelectYearMonth(dayjs(value).format("YYYYMM"))
            }
          />
        </Form.Item>
        <Form.Item
          label="CAP level"
          name="capLevel"
          rules={[
            {
              required: true,
              message: "This field is required!",
            },
          ]}
        >
          <Select
            options={[
              { label: "Light Blue", value: "LightBlue" },
              { label: "Blue", value: "Blue" },
              { label: "Green", value: "Green" },
              { label: "Lime", value: "Lime" },
              { label: "Yellow", value: "Yellow" },
            ]}
            style={{ marginTop: 8 }}
            onChange={(value) => setSelectCAPLevel(value)}
          />
        </Form.Item>
        <Form.Item
          label="Video"
          name="videoFile"
          // valuePropName="fileList"
          // getValueFromEvent={(e) => {
          //   if (Array.isArray(e)) {
          //     return e;
          //   }
          //   return e && e.fileList;
          // }}
        >
          {/* <Upload {...videoProps}>
            <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
              Click to Upload
            </Button>
          </Upload> */}
          <InputCustom placeholder="Vimeo ID" />
        </Form.Item>
        <Form.Item>
          <p
            style={{
              color: "rgba(0,0,0,0.45)",
              lineHeight: "22px",
              marginTop: -40,
            }}
          >
            Enter the ID number of the video uploaded on Vimeo
          </p>
        </Form.Item>
        <Form.Item
          label="HK Parent's guide"
          name="hkPdf"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
          style={{ marginTop: -40 }}
        >
          <Upload
            {...pdfProps}
            onChange={(info) => {
              console.log(info);
              if (info.fileList.length === 0) {
                setHkPdfFileName(undefined);
                return;
              }
              if (info.file.status === "done") {
                setHkPdfFileName(info.file.name);
                uploadFile({
                  variables: {
                    blobs: [
                      {
                        file: info.file.originFileObj,
                        path: AfBlobPath.VesPdf,
                      },
                    ],
                  },
                }).catch((e) => console.log(e));
                message.success(
                  renderAntDMessageConfig(
                    `${info.file.name} file uploaded successfully`
                  )
                );
              } else if (info.file.status === "error") {
                message.error(
                  renderAntDMessageConfig(
                    `${info.file.name} file upload failed.`
                  )
                );
              }
            }}
            onRemove={() => setHkPdfFileName("")}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
              Click to Upload
            </Button>
          </Upload>
        </Form.Item>
        <Form.Item
          label="KR Parent's guide"
          name="krPdf"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            {...pdfProps}
            onChange={(info) => {
              console.log(info);
              if (info.fileList.length === 0) {
                setKrPdfFileName(undefined);
                return;
              }
              if (info.file.status === "done") {
                setKrPdfFileName(info.file.name);
                uploadFile({
                  variables: {
                    blobs: [
                      {
                        file: info.file.originFileObj,
                        path: AfBlobPath.VesPdf,
                      },
                    ],
                  },
                }).catch((e) => console.log(e));
                message.success(
                  renderAntDMessageConfig(
                    `${info.file.name} file uploaded successfully`
                  )
                );
              } else if (info.file.status === "error") {
                message.error(
                  renderAntDMessageConfig(
                    `${info.file.name} file upload failed.`
                  )
                );
              }
            }}
            onRemove={() => setKrPdfFileName("")}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
              Click to Upload
            </Button>
          </Upload>
        </Form.Item>
        <Form.Item
          label="TW Parent's guide"
          name="twPdf"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            {...pdfProps}
            onChange={(info) => {
              console.log(info);
              if (info.fileList.length === 0) {
                setTwPdfFileName(undefined);
                return;
              }
              if (info.file.status === "done") {
                setTwPdfFileName(info.file.name);
                uploadFile({
                  variables: {
                    blobs: [
                      {
                        file: info.file.originFileObj,
                        path: AfBlobPath.VesPdf,
                      },
                    ],
                  },
                }).catch((e) => console.log(e));
                message.success(
                  renderAntDMessageConfig(
                    `${info.file.name} file uploaded successfully`
                  )
                );
              } else if (info.file.status === "error") {
                message.error(
                  renderAntDMessageConfig(
                    `${info.file.name} file upload failed.`
                  )
                );
              }
            }}
            onRemove={() => setTwPdfFileName("")}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
              Click to Upload
            </Button>
          </Upload>
        </Form.Item>
        <Form.Item
          label="Review poster"
          name="posterFile"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload {...posterProps}>
            <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
              Click to Upload
            </Button>
            <p style={{ color: "rgba(0,0,0,0.45)", lineHeight: "22px" }}>
              Formats: PNG only. Max file size: 200KB
            </p>
          </Upload>
        </Form.Item>
        <DrawerFooter
          handleCancel={() => {
            form.resetFields();
            setHkPdfFileName(undefined);
            setKrPdfFileName(undefined);
            setTwPdfFileName(undefined);
            setPosterFileName(undefined);
            setSelectYearMonth(undefined);
            setSelectCAPLevel(undefined);
            setShow(false);
          }}
          isLoading={btnLoading}
        />
      </Form>
    </Drawer>
  );
};

export default GoGoLiveCreateDrawer;
